import React from 'react';

// Base skeleton component with shimmer animation
const SkeletonBase = ({ className = '' }) => (
  <div className={`animate-pulse bg-stone-200 ${className}`} />
);

// Card skeleton for dashboard widgets
export const CardSkeleton = ({ lines = 3 }) => (
  <div className="bg-white rounded-lg shadow-md p-4">
    <SkeletonBase className="h-4 w-1/3 mb-3 rounded" />
    <div className="space-y-2">
      {[...Array(lines)].map((_, i) => (
        <SkeletonBase
          key={i}
          className={`h-3 rounded ${i === lines - 1 ? 'w-4/5' : 'w-full'}`}
        />
      ))}
    </div>
  </div>
);

// Table skeleton
export const TableSkeleton = ({ rows = 5, columns = 4 }) => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    <div className="px-6 py-4 bg-stone-50 border-b">
      <SkeletonBase className="h-4 w-1/4 rounded" />
    </div>
    <div className="divide-y divide-stone-200">
      {[...Array(rows)].map((_, rowIndex) => (
        <div key={rowIndex} className="px-6 py-4">
          <div className="flex items-center space-x-4">
            {[...Array(columns)].map((_, colIndex) => (
              <SkeletonBase
                key={colIndex}
                className={`h-3 rounded ${
                  colIndex === 0 ? 'w-1/4' :
                  colIndex === columns - 1 ? 'w-1/6' :
                  'w-1/3'
                }`}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// List skeleton
export const ListSkeleton = ({ items = 3 }) => (
  <div className="space-y-2">
    {[...Array(items)].map((_, i) => (
      <div key={i} className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-start space-x-3">
          <SkeletonBase className="h-10 w-10 rounded-full flex-shrink-0" />
          <div className="flex-1">
            <SkeletonBase className="h-4 w-3/4 mb-2 rounded" />
            <SkeletonBase className="h-3 w-full mb-1 rounded" />
            <SkeletonBase className="h-3 w-5/6 rounded" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// Form skeleton
export const FormSkeleton = ({ fields = 4 }) => (
  <div className="space-y-4">
    {[...Array(fields)].map((_, i) => (
      <div key={i}>
        <SkeletonBase className="h-3 w-1/4 mb-2 rounded" />
        <SkeletonBase className="h-10 w-full rounded" />
      </div>
    ))}
    <div className="flex space-x-3 pt-4">
      <SkeletonBase className="h-10 w-24 rounded" />
      <SkeletonBase className="h-10 w-24 rounded" />
    </div>
  </div>
);

// Dashboard stat skeleton
export const StatSkeleton = () => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <SkeletonBase className="h-3 w-1/2 mb-2 rounded" />
        <SkeletonBase className="h-8 w-3/4 rounded" />
      </div>
      <SkeletonBase className="h-12 w-12 rounded-full" />
    </div>
  </div>
);

// Chart skeleton
export const ChartSkeleton = ({ height = 'h-64' }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <SkeletonBase className="h-4 w-1/3 mb-4 rounded" />
    <div className={`${height} flex items-end space-x-2`}>
      {[...Array(6)].map((_, i) => (
        <div key={i} className="flex-1 flex flex-col justify-end">
          <SkeletonBase
            className="rounded-t"
            style={{
              height: `${Math.random() * 80 + 20}%`,
              minHeight: '20px'
            }}
          />
        </div>
      ))}
    </div>
  </div>
);

// Property card skeleton
export const PropertyCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden">
    <SkeletonBase className="h-48 w-full" />
    <div className="p-4">
      <SkeletonBase className="h-5 w-3/4 mb-2 rounded" />
      <SkeletonBase className="h-3 w-full mb-1 rounded" />
      <SkeletonBase className="h-3 w-5/6 mb-3 rounded" />
      <div className="flex justify-between items-center">
        <SkeletonBase className="h-4 w-1/3 rounded" />
        <SkeletonBase className="h-6 w-20 rounded-full" />
      </div>
    </div>
  </div>
);

// Room grid skeleton
export const RoomGridSkeleton = ({ count = 6 }) => (
  <div className="grid grid-cols-3 gap-4">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="bg-white rounded-lg shadow p-4">
        <div className="flex justify-between items-start mb-3">
          <SkeletonBase className="h-6 w-16 rounded" />
          <SkeletonBase className="h-5 w-20 rounded-full" />
        </div>
        <SkeletonBase className="h-3 w-full mb-2 rounded" />
        <SkeletonBase className="h-3 w-3/4 mb-2 rounded" />
        <div className="flex justify-between items-center pt-2 border-t">
          <SkeletonBase className="h-4 w-1/3 rounded" />
          <SkeletonBase className="h-4 w-1/4 rounded" />
        </div>
      </div>
    ))}
  </div>
);

// Contract skeleton
export const ContractSkeleton = () => (
  <div className="bg-white rounded-lg shadow-md p-6">
    <div className="flex justify-between items-start mb-4">
      <div>
        <SkeletonBase className="h-6 w-32 mb-2 rounded" />
        <SkeletonBase className="h-4 w-48 rounded" />
      </div>
      <SkeletonBase className="h-8 w-24 rounded-full" />
    </div>
    <div className="grid grid-cols-2 gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i}>
          <SkeletonBase className="h-3 w-20 mb-1 rounded" />
          <SkeletonBase className="h-4 w-full rounded" />
        </div>
      ))}
    </div>
  </div>
);

// Notification skeleton
export const NotificationSkeleton = ({ count = 3 }) => (
  <div className="space-y-2">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="p-4 border-l-4 border-stone-200 bg-stone-50">
        <div className="flex items-start gap-3">
          <SkeletonBase className="h-8 w-8 rounded-full flex-shrink-0" />
          <div className="flex-1">
            <SkeletonBase className="h-4 w-1/3 mb-2 rounded" />
            <SkeletonBase className="h-3 w-full mb-1 rounded" />
            <SkeletonBase className="h-3 w-4/5 rounded" />
            <SkeletonBase className="h-2 w-24 mt-2 rounded" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// Payment skeleton
export const PaymentSkeleton = () => (
  <div className="bg-white rounded-lg shadow p-4">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center space-x-3">
        <SkeletonBase className="h-10 w-10 rounded-full" />
        <div>
          <SkeletonBase className="h-4 w-32 mb-1 rounded" />
          <SkeletonBase className="h-3 w-24 rounded" />
        </div>
      </div>
      <div className="text-right">
        <SkeletonBase className="h-5 w-20 mb-1 rounded" />
        <SkeletonBase className="h-3 w-16 rounded" />
      </div>
    </div>
    <div className="flex justify-between items-center pt-3 border-t">
      <SkeletonBase className="h-6 w-24 rounded-full" />
      <SkeletonBase className="h-8 w-20 rounded" />
    </div>
  </div>
);

// Ticket skeleton
export const TicketSkeleton = () => (
  <div className="bg-white rounded-lg shadow p-4">
    <div className="flex justify-between items-start mb-3">
      <div>
        <SkeletonBase className="h-5 w-24 mb-2 rounded" />
        <SkeletonBase className="h-4 w-48 rounded" />
      </div>
      <div className="flex space-x-2">
        <SkeletonBase className="h-6 w-16 rounded-full" />
        <SkeletonBase className="h-6 w-16 rounded-full" />
      </div>
    </div>
    <SkeletonBase className="h-3 w-full mb-1 rounded" />
    <SkeletonBase className="h-3 w-4/5 mb-3 rounded" />
    <div className="flex items-center justify-between pt-3 border-t">
      <SkeletonBase className="h-3 w-32 rounded" />
      <SkeletonBase className="h-3 w-24 rounded" />
    </div>
  </div>
);

// User profile skeleton
export const UserProfileSkeleton = () => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center space-x-4 mb-6">
      <SkeletonBase className="h-20 w-20 rounded-full" />
      <div className="flex-1">
        <SkeletonBase className="h-6 w-1/3 mb-2 rounded" />
        <SkeletonBase className="h-4 w-1/2 mb-1 rounded" />
        <SkeletonBase className="h-3 w-1/4 rounded" />
      </div>
    </div>
    <div className="space-y-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="flex justify-between">
          <SkeletonBase className="h-3 w-1/4 rounded" />
          <SkeletonBase className="h-3 w-1/3 rounded" />
        </div>
      ))}
    </div>
  </div>
);

export default {
  SkeletonBase,
  CardSkeleton,
  TableSkeleton,
  ListSkeleton,
  FormSkeleton,
  StatSkeleton,
  ChartSkeleton,
  PropertyCardSkeleton,
  RoomGridSkeleton,
  ContractSkeleton,
  NotificationSkeleton,
  PaymentSkeleton,
  TicketSkeleton,
  UserProfileSkeleton
};